import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Button, Dialog, TextField } from "@mui/material";

import AutocompleteDropdown from "../../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import LoadingOverlay from "../../../../../containers/LoadingOverlay/LoadingOverlay";
import DialogTitle from "../../../../../containers/DialogTitle/DialogTitle";
import DialogActions from "../../../../../containers/DialogActions/DialogActions";

import { CompanyLegalEntityRef } from "../../../../../models/company-accounts/company-legal-entities";
import { LegalEntityEventSTO } from "../../../../../models/legal-entity-events";
import { EventTypesApiService } from "../../../../../services/event-types/event-types-api.service";
import { saveLegalEntityEvent } from "../../../../../services/legal-entity-events";

import { Props, Event } from "./interfaces";
import { DialogContent } from "./styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { convertDateToUTC } from "src/utils/time-utils";

const AddNewEvent = ({ legalEntityId, handleNotifyParent }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.document_reports.compliance_documents.assemblies_audits_corporate_changes.add_new_event"; // tRP - translation root path

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [event, setEvent] = useState<LegalEntityEventSTO>({
    isDeleted: false,
    id: uuidv4(),
    companyAccount: null,
    legalEntity: null,
    name: "",
    date: new Date(),
    eventType: null,
    documents: [],
    summary: "",
  });

  useEffect(() => {
    handleChangeEvent("legalEntity", {
      id: legalEntityId,
    } as CompanyLegalEntityRef);
  }, [legalEntityId]);

  const resetEvent = () =>
    setEvent((event) => ({
      ...event,
      id: uuidv4(),
      eventType: null,
      date: new Date(),
      name: "",
    }));

  const handleCloseDialog = () => {
    resetEvent();
    setOpen(false);
  };

  const handleChangeEvent = (field: keyof Event, value: Event[keyof Event]) =>
    setEvent((event) => ({ ...event, [field]: value }));

  const handleSaveEvent = async (event: LegalEntityEventSTO) => {
    setLoading(true);

    try {
      const { error } = await saveLegalEntityEvent({ ...event, date: convertDateToUTC(event.date) });

      if (!error) {
        await handleNotifyParent();
        setOpen(false);
        resetEvent();
      } else {
        setError(error);
      }
    } catch (error) {
      setError(error as string);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="contained"
      >
        {`${t(`${tRP}.open_button`)}`}
      </Button>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
      >
        <LoadingOverlay loading={loading}>
          <DialogTitle
            title={t(`${tRP}.dialog_title`)}
            handleClose={handleCloseDialog}
          />
          <DialogContent>
            <AutocompleteDropdown
              labelKey={`${tRP}.event_fields.event_type`}
              field="eventType"
              serviceApi={new EventTypesApiService()}
              handleChange={handleChangeEvent}
            />
            <DesktopDatePicker
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t(`${tRP}.event_fields.date`)}`}
                />
              )}
              value={event.date}
              onChange={(value) => handleChangeEvent("date", value)}
            />
            <TextField
              label={`${t(`${tRP}.event_fields.name`)}`}
              value={event.name}
              onChange={({ target: { value } }) => handleChangeEvent("name", value)}
            />
          </DialogContent>
          <DialogActions
            handleCancel={handleCloseDialog}
            handleSave={() => handleSaveEvent(event)}
            error={error}
          />
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default AddNewEvent;
